import React from "react";
import { Megaphone, ExternalLink, Plus, Pencil, Trash2, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";
import AdvertEditorModal from "../components/site/AdvertEditorModal";
import { useAuth } from "../context/AuthContext";
import { IMAGES } from "../lib/images";

function AdvertCard({ a, isAdmin, onEdit, onDelete }) {
  const isVideo = a.media_type === "video";
  const trackClick = () => { api.post(`/adverts/${a.id}/click`).catch(() => {}); };

  return (
    <div className="group relative h-full flex flex-col rounded-2xl border border-cream/12 bg-night-surface overflow-hidden transition-colors hover:border-gold/40" data-testid={`advert-card-${a.id}`}>
      {isAdmin && (
        <div className="absolute top-3 right-3 z-20 flex gap-2">
          <button onClick={(e) => { e.stopPropagation(); onEdit(a); }} data-testid={`advert-edit-${a.id}`} title="Edit" className="grid place-items-center w-9 h-9 rounded-full bg-night/80 text-cream hover:bg-forest backdrop-blur transition-colors"><Pencil className="w-4 h-4" /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(a); }} data-testid={`advert-delete-${a.id}`} title="Delete" className="grid place-items-center w-9 h-9 rounded-full bg-night/80 text-cream hover:bg-red-500 backdrop-blur transition-colors"><Trash2 className="w-4 h-4" /></button>
        </div>
      )}
      <div className="relative aspect-video overflow-hidden bg-black shrink-0">
        {isVideo ? (
          <video
            src={a.media_url}
            poster={a.poster || undefined}
            controls
            playsInline
            preload="metadata"
            className="w-full h-full object-contain bg-black"
            data-testid={`advert-video-${a.id}`}
          />
        ) : a.link_url ? (
          <a href={a.link_url} target="_blank" rel="noopener noreferrer" onClick={trackClick} className="block w-full h-full cursor-pointer" data-testid={`advert-image-link-${a.id}`}>
            <img src={a.media_url} alt={a.title || "Advert"} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid={`advert-image-${a.id}`} />
          </a>
        ) : (
          <img src={a.media_url} alt={a.title || "Advert"} loading="lazy" className="w-full h-full object-cover" data-testid={`advert-image-${a.id}`} />
        )}
        {!a.published && isAdmin && <span className="absolute top-3 left-3 z-10 text-xs bg-night/80 text-cream/70 rounded px-2 py-0.5 pointer-events-none">Hidden</span>}
        {a.featured && <span className="absolute top-3 left-3 z-10 text-xs bg-gold text-night font-semibold rounded-full px-2.5 py-1 pointer-events-none">★ Featured</span>}
      </div>
      {(a.title || a.description || a.link_url || isAdmin) && (
        <div className="flex flex-col flex-1 p-6">
          {a.title && <h3 className="font-serif text-xl text-cream leading-tight mb-2">{a.title}</h3>}
          {a.description && <p className="text-cream/60 text-sm leading-relaxed mb-4">{a.description}</p>}
          {a.link_url && (
            <a
              href={a.link_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackClick}
              data-testid={`advert-link-${a.id}`}
              className="mt-auto inline-flex items-center justify-center gap-2 bg-gold text-night font-semibold px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors self-start"
            >
              {a.link_label || "Learn More"} <ExternalLink className="w-4 h-4" />
            </a>
          )}
          {isAdmin && (
            <p className="text-xs text-cream/50 mt-3 flex items-center gap-1.5" data-testid={`advert-clicks-${a.id}`}>
              <MousePointerClick className="w-3.5 h-3.5 text-gold" /> {a.clicks || 0} click{(a.clicks || 0) === 1 ? "" : "s"}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function Adverts() {
  useSEO(
    "Adverts — ÀsÀNigHT Worldwide",
    "Adverts, promotions and community announcements from ÀsÀNigHT Worldwide and partners."
  );
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [adverts, setAdverts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null); // null | 'new' | advert

  const load = React.useCallback(() => {
    setLoading(true);
    api.get(isAdmin ? "/admin/adverts" : "/adverts")
      .then((r) => setAdverts(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAdmin]);

  React.useEffect(() => { load(); }, [load]);

  const removeAdvert = async (a) => {
    if (!window.confirm("Delete this advert?")) return;
    try { await api.delete(`/admin/adverts/${a.id}`); toast.success("Advert deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div className="bg-night text-cream min-h-screen">
      <header className="relative grain-overlay">
        <div className="absolute inset-0">
          <img src={IMAGES.marketScene || IMAGES.textileGeometric} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-night/85 via-night/80 to-night" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 pt-28 pb-16 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 mb-4"><Megaphone className="w-4 h-4 text-gold" /><p className="uppercase tracking-[0.25em] text-xs text-gold-soft">Adverts</p></div>
            <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] mb-5" data-testid="adverts-title">Adverts & Announcements</h1>
            <p className="text-cream/75 text-lg leading-relaxed max-w-2xl mx-auto">
              Promotions, events and community announcements from ÀsÀNigHT Worldwide and our partners.
            </p>
          </Reveal>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 pb-24 relative z-10">
        {isAdmin && (
          <div className="flex justify-end mb-6 mt-8">
            <button onClick={() => setEditing("new")} data-testid="adverts-add-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
              <Plus className="w-4 h-4" /> Post Advert
            </button>
          </div>
        )}

        {loading ? (
          <p className="text-cream/50 py-16 text-center">Loading adverts…</p>
        ) : adverts.length === 0 ? (
          <div className="text-center py-20" data-testid="adverts-empty">
            <Megaphone className="w-10 h-10 text-cream/30 mx-auto mb-4" />
            <p className="text-cream/50">No adverts posted yet — please check back soon.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {adverts.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 0.05}>
                <AdvertCard a={a} isAdmin={isAdmin} onEdit={setEditing} onDelete={removeAdvert} />
              </Reveal>
            ))}
          </div>
        )}
      </div>

      {editing && <AdvertEditorModal advert={editing} onClose={() => setEditing(null)} onSaved={load} />}

      <Newsletter />
    </div>
  );
}
